import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { apiAction } from "@client/lib/actions";
import { goTo } from "@client/lib/navigation";
import { type EntityRef, Status } from "@client/types";
import { type AppState } from "@client/types/AppState";
import { type AnyAction, type Dispatch } from "redux";
import { initAction } from "../actions";
import api from "./api";
import { localStateSelector } from "./selectors";
import { type Cart } from "./types";

export enum Actions {
  InitBegin = "MODULE_CART_INIT_BEGIN",
  InitFailure = "MODULE_CART_INIT_FAILURE",
  InitSuccess = "MODULE_CART_INIT_SUCCESS",

  CheckoutBegin = "MODULE_CART_CHECKOUT_BEGIN",
  CheckoutFailure = "MODULE_CART_CHECKOUT_FAILURE",
  CheckoutSuccess = "MODULE_CART_CHECKOUT_SUCCESS",

  UpdateCart = "MODULE_CART_UPDATE_CART",

  RemoveFromCartBegin = "MODULE_CART_REMOVE_FROM_CART_BEGIN",
  RemoveFromCartFailure = "MODULE_CART_REMOVE_FROM_CART_FAILURE",
  RemoveFromCartSuccess = "MODULE_CART_REMOVE_FROM_CART_SUCCESS"
}

export type ProceedToCheckoutArgs = EntityRef & {
  cart: Cart.Cart;
};
const _checkout = apiAction<AppState, ProceedToCheckoutArgs, void>({
  types: {
    begin: Actions.CheckoutBegin,
    failure: Actions.CheckoutFailure,
    success: [
      Actions.CheckoutSuccess,
      ({ getState, params }) => {
        goTo(localStateSelector(getState(), params).checkoutUrl);
      }
    ]
  },
  action: async (state: AppState, dispatch: Dispatch<AnyAction>, args: ProceedToCheckoutArgs) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);

    return api.checkout(apiInfo, {
      portalId: state.portal.id,
      items: args.cart.items.map((x) => ({ id: x.id, quantity: x.quantity }))
    });
  }
});

export const checkout = (ref: EntityRef) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();
  const localState = localStateSelector(state, ref);
  if (localState.status === Status.Ready) {
    dispatch(_checkout({ ...ref, cart: localState.cart }));
  }
};

export type InitBeginActionData = EntityRef & {};
export type InitSuccessActionData = Cart.Api.InitResponse;
export const _init = initAction<AppState, InitBeginActionData, Cart.Api.InitResponse>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: (state: AppState, dispatch: Dispatch<AnyAction>, args: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);

    return api.init(apiInfo, { portalId: state.portal.id });
  }
});

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (localState.status === Status.None) {
    dispatch(_init(args));
  }
};

export type UpdateCartActionData = EntityRef & {
  cart: Cart.Cart;
};
export const updateCart = (ref: EntityRef, cart: Cart.Cart) => ({
  type: Actions.UpdateCart,
  data: <UpdateCartActionData>{
    ...ref,
    cart
  }
});

export const _removeFromCart = apiAction<AppState, RemoveFromCardArgs, void>({
  types: {
    begin: Actions.RemoveFromCartBegin,
    failure: Actions.RemoveFromCartFailure,
    success: Actions.RemoveFromCartSuccess
  },
  action: async (state: AppState, dispatch: Dispatch<AnyAction>, args: RemoveFromCardArgs) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    return api.remove(apiInfo, args.productId);
  }
});

export type RemoveFromCardArgs = EntityRef & {
  productId: string;
};
export const removeFromCart =
  (ref: EntityRef, productId: string) => (dispatch: Dispatch<any>, getState: () => AppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);

    if (localState.status === Status.Ready) {
      dispatch(_removeFromCart({ ...ref, productId }));
    }
  };
